import type { PremadeCategory } from '../types/premadeCase';

export interface PremadeCategoryPageConfig {
  slug: string;
  category: PremadeCategory;
  title: string;
  headline: string;
  description: string;
  metaDescription: string;
  highlights: string[];
}

export const PREMADE_CATEGORY_PAGES: Record<string, PremadeCategoryPageConfig> = {
  'gun-cases': {
    slug: 'gun-cases',
    category: 'GUN',
    title: 'Custom Gun Cases & Foam Inserts - Rifles, Pistols & Firearms',
    headline: 'Custom Gun Cases & Foam Inserts',
    description:
      'Get custom gun cases and foam inserts for pistols, rifles, and firearms. Protect your gear with precision-fit closed-cell polyethylene foam — from single-pistol range cases to multi-gun instructor kits and rifle transports.',
    metaDescription:
      'Get custom gun cases and foam inserts for pistols, rifles & firearms. Protect your gear with precision-fit foam. Order your custom cut gun case online today!',
    highlights: [
      'Pistol, revolver, and rifle layouts with magazine and accessory bays',
      'Pelican, SKB, Nanuk, Doro, HPRC, and Seahorse case shells',
      'Foam-only upgrades for cases you already own',
      'CNC precision-cut polyethylene foam — no pick-and-pluck gaps',
    ],
  },
  'camera-video': {
    slug: 'camera-video',
    category: 'CAMERA_VIDEO',
    title: 'Camera & Video Case Foam Inserts - DSLR, Cinema & Lens Kits',
    headline: 'Camera & Video Foam Cases',
    description:
      'Finished foam layouts for cinema cameras, DSLRs, lens kits, and video rigs. Each build pairs your gear with a tested Pelican, Nanuk, or SKB shell so you can copy the layout in CaseBuilder or request manufacturing.',
    metaDescription:
      'Browse premade camera and video foam case inserts for DSLRs, cinema bodies, and lens kits. Precision-cut layouts for Pelican, Nanuk, and SKB cases.',
    highlights: [
      'Body + lens + accessory bays sized to real kit combinations',
      'Pelican, Nanuk, SKB, and HPRC shells in the portfolio',
      'Convoluted lid foam and multi-layer tray options',
      'Copy any layout as your CaseBuilder starting point',
    ],
  },
  'drone-cases': {
    slug: 'drone-cases',
    category: 'DRONE',
    title: 'Drone Case Foam Inserts - DJI, FPV & Controller Kits',
    headline: 'Drone Foam Cases',
    description:
      'Protect drones, controllers, batteries, and FPV accessories with premade foam inserts sized to popular airframes. Browse finished builds for compact travel cases and full kit transports.',
    metaDescription:
      'Premade drone case foam inserts for DJI, FPV, and controller kits. Precision-cut polyethylene foam for Pelican-class and compact travel cases.',
    highlights: [
      'Airframe, remote, battery, and prop cutouts in one layout',
      'Compact and full-size case options',
      'Shock-protected bays for gimbals and cameras',
      'Open in the editor to tweak for your exact kit',
    ],
  },
  'audio-visual': {
    slug: 'audio-visual',
    category: 'AUDIO_VISUAL',
    title: 'Audio & Visual Case Foam - Mixers, Mics & Production Gear',
    headline: 'Audio / Visual Foam Cases',
    description:
      'Premade foam layouts for mixers, microphones, wireless rigs, and production accessories. Keep AV kits organized and road-ready with CNC-cut inserts matched to your case shell.',
    metaDescription:
      'Browse premade audio and visual equipment foam case inserts. Custom-cut layouts for mixers, mics, wireless systems, and production gear.',
    highlights: [
      'Mixer, mic, and wireless receiver bays',
      'Cable and accessory compartments',
      'Road-case and compact Pelican-class options',
      'Export DXF or request a manufacturing quote',
    ],
  },
  'computers-tablets': {
    slug: 'computers-tablets',
    category: 'COMPUTERS_TABLETS',
    title: 'Laptop & Tablet Case Foam Inserts - Computers & Hard Drives',
    headline: 'Computers & Tablets Foam Cases',
    description:
      'Finished foam inserts for laptops, tablets, hard drives, and mobile computing kits. Precision-cut bays keep electronics secure in transit without pick-and-pluck compromise.',
    metaDescription:
      'Premade laptop, tablet, and hard-drive foam case inserts. Precision-cut polyethylene foam for portable computers and storage kits.',
    highlights: [
      'Laptop, tablet, and external drive cutouts',
      'Charger and cable accessory bays',
      'Anti-static foam options on request',
      'Sized to Pelican, Nanuk, and SKB interiors',
    ],
  },
  'astronomy-scientific': {
    slug: 'astronomy-scientific',
    category: 'ASTRONOMY_SCIENTIFIC',
    title: 'Custom Astronomy Cases for Telescopes & Scientific Instruments',
    headline: 'Custom Astronomy Cases for Telescopes & Scientific Instruments',
    description:
      'If you are an astronomy enthusiast or work with scientific instruments, protect telescopes, eyepieces, lenses, chronographs, and field measurement gear with precision-cut foam inserts. Browse premade layouts for Pelican, HPRC, and specialty shells — or open any design in CaseBuilder to customize depths and accessory bays.',
    metaDescription:
      'Custom astronomy cases and foam inserts for telescopes, eyepieces, and scientific instruments. Protect delicate gear with precision-fit foam. Order online today!',
    highlights: [
      'Telescope mounts, eyepiece sets, and chronograph layouts',
      'Pelican, HPRC, Storm, Vault, and aluminum case shells',
      'Shock isolation for optics and delicate measurement tools',
      'Copy any premade layout as your CaseBuilder starting point',
    ],
  },
  'balisong-cases': {
    slug: 'balisong-cases',
    category: 'BALISONG',
    title: 'Balisong & Knife Collection Case Foam Inserts',
    headline: 'Balisong Foam Cases',
    description:
      'Display and transport balisongs and knife collections with premade foam inserts that hold each piece securely without blade contact. Browse finished layouts sized to popular case depths.',
    metaDescription:
      'Premade balisong and knife collection foam case inserts. Precision-cut bays for secure display and transport.',
    highlights: [
      'Individual blade bays with finger-lift notches',
      'Collection and single-knife layouts',
      'Compact travel and display case options',
      'Customize spacing in the online editor',
    ],
  },
  'beer-wine-spirits': {
    slug: 'beer-wine-spirits',
    category: 'BEER_WINE_SPIRITS',
    title: 'Beer, Wine & Spirits Case Foam Inserts - Bottle Transport',
    headline: 'Beer, Wine & Spirits Foam Cases',
    description:
      'Premade foam inserts for beer, wine, and spirits transport kits. Hold bottles, growlers, and tasting accessories upright with CNC-cut bays matched to your case interior.',
    metaDescription:
      'Premade beer, wine, and spirits bottle foam case inserts. Precision-cut layouts for tasting kits and bottle transport cases.',
    highlights: [
      'Bottle and growler cutouts with retention lips',
      'Accessory bays for openers and tasting gear',
      'Road-case and Pelican-class options',
      'Request quotes for custom bottle counts',
    ],
  },
  'watch-cases': {
    slug: 'watch-cases',
    category: 'WATCH',
    title: 'Watch Collection Case Foam Inserts - Luxury & Sport Watches',
    headline: 'Watch Collection Foam Cases',
    description:
      'Premade foam layouts for single watches and multi-piece collections. Cushioned bays protect cases and bracelets during travel, trade shows, and daily carry.',
    metaDescription:
      'Premade watch collection foam case inserts. Precision-cut bays for luxury and sport watches in protective hard cases.',
    highlights: [
      'Single-watch and multi-slot collection layouts',
      'Cushioned bays for case and bracelet protection',
      'Compact travel and display configurations',
      'Adjust slot spacing in CaseBuilder',
    ],
  },
};

/** Maps enum category → URL slug for category landing pages. */
export const CATEGORY_TO_SLUG: Record<PremadeCategory, string> = Object.fromEntries(
  Object.values(PREMADE_CATEGORY_PAGES).map((p) => [p.category, p.slug]),
) as Record<PremadeCategory, string>;

export function getPremadeCategoryPage(slug: string): PremadeCategoryPageConfig | undefined {
  return PREMADE_CATEGORY_PAGES[slug];
}

export function getCategorySlug(category: PremadeCategory): string {
  return CATEGORY_TO_SLUG[category];
}

export const PREMADE_CATEGORY_PAGE_SLUGS = Object.keys(PREMADE_CATEGORY_PAGES);
